import { useEffect, useRef, useState } from 'react';
import {
  Bell, Car, ClipboardCheck, IdCard, UserRoundSearch,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { notificationService } from '../../services';

const notificationIcons = {
  DUPLICATE_DETECTED: UserRoundSearch,
  DUPLICATE_RESOLVED: UserRoundSearch,
  SHEET_READY_FOR_REVIEW: ClipboardCheck,
  SHEET_VALIDATED: ClipboardCheck,
  SHEET_INVALIDATED: ClipboardCheck,
  VEHICLE_FULL: Car,
  DRIVER_LICENSE_EXPIRING: IdCard,
  MOBILIZATION_ASSIGNED: Car,
  MOBILIZATION_STATUS_CHANGED: Car,
  PASSWORD_RESET: IdCard,
};

const modulePaths = {
  duplicates: '/duplicados',
  sheets: '/control-planillas',
  vehicles: '/moviles',
  drivers: '/conductores',
  accounts: '/usuarios',
};

function NotificationMenu() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [error, setError] = useState('');
  const ref = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    Promise.all([
      notificationService.getUnreadByUser(),
      notificationService.getUnreadCount(),
    ])
      .then(([listResponse, countResponse]) => {
        if (!mounted) return;
        setNotifications(listResponse.data);
        setUnreadCount(countResponse.data.count);
        setError('');
      })
      .catch((requestError) => {
        if (!mounted) return;
        setNotifications([]);
        setError(requestError.message);
      });
    const close = (event) => !ref.current?.contains(event.target) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => {
      mounted = false;
      document.removeEventListener('mousedown', close);
    };
  }, [user.id]);

  const markAsRead = async (notification) => {
    await notificationService.markAsRead(notification.id);
    setUnreadCount((count) => Math.max(0, count - (notification.read ? 0 : 1)));
    setNotifications((items) => showAll
      ? items.map((item) => item.id === notification.id ? { ...item, read: true } : item)
      : items.filter((item) => item.id !== notification.id));
    const path = modulePaths[notification.module];
    if (path) {
      setOpen(false);
      navigate(path);
    }
  };

  const viewAll = async () => {
    const response = await notificationService.getByUser(user.id);
    setNotifications(response.data);
    setShowAll(true);
  };

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((value) => !value)} className="relative grid size-10 place-items-center rounded-control text-content-muted transition hover:bg-slate-100 hover:text-content-primary" aria-label="Notificaciones" aria-expanded={open}>
        <Bell className="size-5" />
        {unreadCount > 0 && <span className="absolute right-2 top-2 size-2 rounded-full border-2 border-white bg-danger-500" />}
      </button>
      {open && (
        <div className="absolute right-0 top-12 z-50 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-card border bg-white shadow-elevated">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <p className="text-sm font-semibold text-content-primary">Notificaciones</p>
            <span className="text-xs font-medium text-primary-700">{unreadCount} nuevas</span>
          </div>
          <div className="divide-y">
            {error && <p className="px-4 py-4 text-center text-sm text-danger-600">{error}</p>}
            {notifications.map((notification) => {
              const Icon = notificationIcons[notification.type] || Bell;
              return (
                <button key={notification.id} onClick={() => markAsRead(notification)} className="flex w-full gap-3 px-4 py-3 text-left hover:bg-slate-50">
                  <span className="grid size-9 shrink-0 place-items-center rounded-full bg-primary-50 text-primary-700">
                    <Icon className="size-4" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-content-primary">{notification.title}</span>
                    <span className="mt-0.5 block text-xs text-content-muted">{notification.message || notification.detail}</span>
                    <span className="mt-1 block text-[11px] text-slate-400">{notification.read ? 'Leída' : 'Sin leer'}</span>
                  </span>
                </button>
              );
            })}
            {notifications.length === 0 && <p className="px-4 py-8 text-center text-sm text-content-muted">No hay notificaciones nuevas.</p>}
          </div>
          {showAll && unreadCount > 0 && <button onClick={async () => { await notificationService.markAllAsRead(); setUnreadCount(0); setNotifications((items) => items.map((item) => ({ ...item, read: true }))); }} className="w-full border-t px-4 py-2.5 text-sm font-medium text-primary-700 hover:bg-primary-50">Marcar todas como leídas</button>}
          {!showAll && <button onClick={viewAll} className="w-full border-t px-4 py-2.5 text-sm font-medium text-primary-700 hover:bg-primary-50">Ver todas</button>}
        </div>
      )}
    </div>
  );
}

export default NotificationMenu;
